import {
  ClusterRevisionForm,
  type ClusterRevisionFormType,
  RevisionFormMode,
} from '@/features/ClusterConfigs/components/ClusterRevisionForm/ClusterRevisionForm'
import { getClusterRevisionFormSchema } from '@/features/ClusterConfigs/components/ClusterRevisionForm/schema'
import { Field, Input, VStack } from '@chakra-ui/react'
import type {
  FieldApi,
  ReactFormExtendedApi,
  StandardSchemaV1,
} from '@tanstack/react-form'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const getNewClusterFormSchema = (t: TFunction) =>
  getClusterRevisionFormSchema(t).extend({
    organizationId: z
      .string()
      .min(1, String(t('validation.required')))
      .length(
        6,
        String(t('newClusterForm.fields.organizationId.validation.length')),
      )
      .regex(
        /^[a-z0-9]{6}$/,
        String(
          t(
            'newClusterForm.fields.organizationId.validation.allowedCharacters',
          ),
        ),
      ),
  })

type FormValues = z.infer<ReturnType<typeof getNewClusterFormSchema>>

// Tanstack form does not provide a nice way for typing a form
export type NewClusterFormType = ReactFormExtendedApi<
  FormValues,
  undefined,
  StandardSchemaV1<FormValues, unknown>,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  StandardSchemaV1<FormValues, unknown>,
  undefined,
  undefined,
  undefined
>

export function NewClusterForm({
  form,
}: Readonly<{ form: NewClusterFormType }>) {
  const { t } = useTranslation()

  return (
    <form style={{ width: '100%' }}>
      <VStack w="full" gap={4}>
        <ClusterRevisionForm
          mode={RevisionFormMode.NEW_CLUSTER}
          // Type casting because TanstackForm has terrible TS public types
          form={form as unknown as ClusterRevisionFormType}
        >
          <form.Field name="organizationId">
            {(field) => {
              const { errorMessage, isInvalid } = getFieldMeta(field)
              return (
                <Field.Root invalid={isInvalid}>
                  <Field.Label htmlFor="organization-id">
                    {t('newClusterForm.fields.organizationId.label')}
                  </Field.Label>
                  <Input
                    id="organization-id"
                    name="organization-id"
                    placeholder={String(
                      t('newClusterForm.fields.organizationId.placeholder'),
                    )}
                    backgroundColor="bg"
                    borderColor={isInvalid ? 'content.error' : 'border'}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid}
                  />
                  {isInvalid && errorMessage ? (
                    <Field.ErrorText>{errorMessage}</Field.ErrorText>
                  ) : (
                    <Field.HelperText>
                      {t('newClusterForm.fields.organizationId.helperText')}
                    </Field.HelperText>
                  )}
                </Field.Root>
              )
            }}
          </form.Field>
        </ClusterRevisionForm>
      </VStack>
    </form>
  )
}

// @ts-expect-error FieldApi expects 12 arguments
function getFieldMeta(field: FieldApi<FormValues>): {
  errorMessage?: string
  isInvalid: boolean
} {
  const errorMessage = field.state.meta.errors[0]?.message
  const isInvalid = field.state.meta.isTouched && !!errorMessage

  return { errorMessage, isInvalid }
}
